import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchWordPressPostById, fetchWordPressPosts } from '../../api/wordpress'; // API関数をインポート
import styled from '@emotion/styled';
import { BlogArticle } from '../../types/blog'; // BlogArticleをインポート
import * as cheerio from 'cheerio';

// --- ヘルパー関数 ---

/**
 * HTMLコンテンツからテキストを抽出し、読了時間を計算する
 * @param htmlContent 記事のHTMLコンテンツ
 * @returns 読了時間（分）
 */
const calculateReadingTime = (htmlContent: string): number => {
  const text = htmlContent.replace(/<[^>]*>/g, ''); // HTMLタグを除去
  const wordCount = text.trim().split(/\s+/).length;
  const wordsPerMinute = 500; // 一般的な読書速度
  return Math.ceil(wordCount / wordsPerMinute);
};

/**
 * HTMLコンテンツから目次を生成し、見出しにIDを付与する
 * @param htmlContent 記事のHTMLコンテンツ
 * @returns 目次データと処理済みHTMLコンテンツ
 */
const generateTocAndContent = (htmlContent: string) => {
  const $ = cheerio.load(htmlContent);
  const toc: { id: string; text: string; level: number }[] = [];
  
  $('h2, h3').each((index, element) => {
    const heading = $(element);
    const text = heading.text();
    const level = parseInt(element.tagName.substring(1), 10);
    const id = `toc-${index}-${text.toLowerCase().replace(/\s+/g, '-')}`;
    
    heading.attr('id', id);
    toc.push({ id, text, level });
  });

  return { toc, content: $.html() };
};


// --- スタイル付きコンポーネント ---

const BlogDetailContainer = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-bottom: 20px;

  &:hover {
    background-color: #0056b3;
  }
`;

const BlogTitle = styled.h1`
  font-size: 2em;
  margin-bottom: 10px;
  color: #333;
`;

const CategoryTag = styled(Link)`
  display: inline-block;
  padding: 5px 15px;
  color: black;
  border: 1px solid #007bff;
  border-radius: 20px;
  text-decoration: none;
  font-size: 0.9em;
  margin-bottom: 15px;
  margin-left: 15px;
  transition: background-color 0.3s;

  &:hover {
    color: white;
    background-color: #0056b3;
  }
`;

const PostInfo = styled.div`
  font-size: 0.9em;
  color: #666;
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
`;

const TableOfContentsContainer = styled.div`
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  transition: all 0.3s ease-in-out;

  h2 {
    margin-top: 0;
    margin-bottom: 15px;
    font-size: 1.4em;
    cursor: pointer;
    user-select: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const TocList = styled.ul`
  padding-left: 0;
  list-style: none;
`;

const TocItem = styled.li<{ level: number }>`
  margin-bottom: 10px;
  padding-left: ${props => (props.level - 2) * 20}px; // h2はインデントなし

  a {
    color: #333;
    text-decoration: none;
    &:hover {
      color: #007bff;
      text-decoration: underline;
    }
  }
`;

const BlogContent = styled.div`
  font-size: 1.1em;
  line-height: 1.8;
  color: #333;

  h1, h2, h3, h4, h5, h6 {
    color: #111;
    margin-top: 2em;
    margin-bottom: 1em;
    line-height: 1.4;
  }

  h1 { font-size: 2.2em; }
  h2 { font-size: 1.8em; }
  h3 { font-size: 1.5em; }
  h4 { font-size: 1.2em; }

  p {
    margin-bottom: 1.5em;
  }

  a {
    color: #007bff;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  ul, ol {
    margin-bottom: 1.5em;
    padding-left: 1.5em;
  }

  li {
    margin-bottom: 0.5em;
  }

  blockquote {
    border-left: 4px solid #eee;
    padding-left: 1em;
    margin: 2em 0;
    color: #666;
    font-style: italic;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1.5em 0;
  }

  code {
    background-color: #f0f0f0;
    padding: 0.2em 0.4em;
    margin: 0 0.2em;
    border-radius: 3px;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
    font-size: 0.95em;
  }

  pre {
    background-color: #2d2d2d;
    color: #f8f8f2;
    padding: 1em;
    border-radius: 8px;
    overflow-x: auto;
    margin: 2em 0;

    code {
      background-color: transparent;
      padding: 0;
      margin: 0;
      font-size: 1em;
    }
  }
`;

const RecommendedSection = styled.div`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #eee;
`;

const RecommendedTitle = styled.h2`
  font-size: 1.8em;
  color: #333;
  margin-bottom: 30px;
  text-align: center;
`;

const RecommendedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 25px;
`;

const RecommendedCard = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
`;

const RecommendedCardImage = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
`;

const RecommendedCardContent = styled.div`
  padding: 15px;
`;

const RecommendedCardTitle = styled.h3`
  font-size: 1.1em;
  color: #333;
  margin: 0;
`;


const BlogPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [recommendedArticles, setRecommendedArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [readingTime, setReadingTime] = useState(0);
  const [tableOfContents, setTableOfContents] = useState<{ id: string; text: string; level: number }[]>([]);
  const [processedContent, setProcessedContent] = useState('');
  const [isTocOpen, setIsTocOpen] = useState(true); // 目次の開閉状態


  useEffect(() => {
    // ページトップにスクロール
    window.scrollTo(0, 0);

    const fetchArticleData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        // 現在の記事と全記事を並行して取得
        const [foundArticle, allPosts] = await Promise.all([
          fetchWordPressPostById(id),
          fetchWordPressPosts()
        ]);
        
        setArticle(foundArticle);

        if (foundArticle) {
          // 読了時間、目次、処理済みコンテンツを生成
          const time = calculateReadingTime(foundArticle.content);
          const { toc, content } = generateTocAndContent(foundArticle.content);
          
          setReadingTime(time);
          setTableOfContents(toc);
          setProcessedContent(content);

          // おすすめ記事を生成（現在の記事とカテゴリが同じものを優先、なければ最新）
          const recommendations = allPosts.articles
            .filter(post => post.id !== foundArticle.id) // 現在の記事を除く
            .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()) // 最新順にソート
            .slice(0, 3); // 3件取得
          setRecommendedArticles(recommendations);
        }

      } catch (error) {
        console.error('ブログ記事の取得に失敗しました:', error);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };

    fetchArticleData();
  }, [id]);

  if (loading) {
    return <p>記事を読み込んでいます...</p>;
  }

  if (!article) {
    return <p>記事が見つかりませんでした。</p>;
  }

  // 記事のHTMLコンテンツからディスクリプションを生成する
  const createDescription = (htmlContent: string) => {
    const text = htmlContent.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    return text.length > 120 ? text.substring(0, 120) + '…' : text;
  };

  return (
    <BlogDetailContainer>
      <Helmet>
        <title>{`${article.title} | LudyWorks`}</title>
        <meta name="description" content={createDescription(article.content)} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={createDescription(article.content)} />
        <meta property="og:url" content={`https://ludyworks.com/${article.id}`} />
        <meta property="og:image" content={article.imageUrl} />
      </Helmet>
      <BackButton onClick={() => navigate('/blog')}>一覧に戻る</BackButton>
      
      {/* カテゴリータグ */}
      {article?.category && (
        <CategoryTag to={`/blog?category=${article.category}`}>{article.category}</CategoryTag>
      )}

      {/* タイトル */}
      <BlogTitle>{article?.title}</BlogTitle>

      {/* 投稿日時と読了時間 */}
      <PostInfo>
        <span>投稿日: {new Date(article?.publishedAt || '').toLocaleDateString()}</span>
        <span>読了時間: 約{readingTime}分</span>
      </PostInfo>

      {/* 目次 */}
      {tableOfContents.length > 0 && (
        <TableOfContentsContainer>
          <h2 onClick={() => setIsTocOpen(!isTocOpen)}>
            目次
            <span>{isTocOpen ? '[-]' : '[+]'}</span>
          </h2>
          {isTocOpen && (
            <TocList>
              {tableOfContents.map(item => (
                <TocItem key={item.id} level={item.level}>
                  <a href={`#${item.id}`}>{item.text}</a>
                </TocItem>
              ))}
            </TocList>
          )}
        </TableOfContentsContainer>
      )}

      {/* 記事本文 */}
      <BlogContent dangerouslySetInnerHTML={{ __html: processedContent }} />

      {/* おすすめ記事 */}
      {recommendedArticles.length > 0 && (
        <RecommendedSection>
          <RecommendedTitle>おすすめ記事</RecommendedTitle>
          <RecommendedGrid>
            {recommendedArticles.map(recArticle => (
              <RecommendedCard key={recArticle.id} to={`/${recArticle.id}`}>
                <RecommendedCardImage src={recArticle.imageUrl} alt={recArticle.title} />
                <RecommendedCardContent>
                  <RecommendedCardTitle>{recArticle.title}</RecommendedCardTitle>
                </RecommendedCardContent>
              </RecommendedCard>
            ))}
          </RecommendedGrid>
        </RecommendedSection>
      )}
    </BlogDetailContainer>
  );
};

export default BlogPage;
